import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, ChevronRight } from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useGarment } from "../../hooks/useGarment";
import { useGarmentDesigns } from "../../hooks/useGarmentDesigns";

function designImages(design) {
  const gallery = (design?.images || [])
    .map((item) => typeof item === "string" ? { url: item, label: "" } : {
      url: item?.websiteUrl || item?.url || item?.imageUrl || "",
      label: item?.label || item?.viewType || "",
    })
    .filter((item) => item.url);

  if (gallery.length > 0) return gallery;
  return design?.imageUrl ? [{ url: design.imageUrl, label: "Primary view" }] : [];
}

export default function DesignDetailPage() {
  const { categoryId, sectionId, garmentId, designId } = useParams();
  const navigate = useNavigate();
  const { garment } = useGarment(garmentId);
  const { designs, designsLoading, designsError } = useGarmentDesigns(garmentId);
  const [imageIndex, setImageIndex] = useState(0);

  const design = useMemo(
    () => designs.find((item) => item.id === designId),
    [designs, designId]
  );
  const images = useMemo(() => designImages(design), [design]);

  const selectDesign = () => {
    sessionStorage.setItem("stichb_design_draft", JSON.stringify({
      source: "catalog_design",
      designId: design.id,
      garmentId,
      garmentName: garment?.name || design.garmentName || "",
      designName: design.name,
      basePrice: Number(design.basePrice || design.startingPrice || 0),
      description: design.description || "",
      imageUrl: design.imageUrl || images[0]?.url || "",
      images: design.images || [],
      recommendedFabricTypeIds: design.recommendedFabricTypeIds || [],
      fabricRequirements: design.fabricRequirements || [],
    }));
    navigate(`/category/${categoryId}/section/${sectionId}/item/${garmentId}/design-confirmation`);
  };

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />
      <main className="pt-28 pb-24">
        <section className="max-w-[1200px] mx-auto px-6 md:px-12">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]">
            <ArrowLeft size={16} /> Back to designs
          </button>

          {designsLoading ? (
            <div className="card-soft p-10 mt-8 animate-pulse"><div className="h-96 rounded bg-[#FFE7D2]" /></div>
          ) : designsError || !design ? (
            <div className="card-soft p-10 mt-8 text-center">
              <h1 className="font-display text-3xl">Design could not be loaded</h1>
              <p className="text-[#5b5a56] mt-3">It may no longer be available for this garment.</p>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
              <div className="card-soft overflow-hidden bg-white">
                <div className="relative aspect-[4/5] bg-[#FFF3E8] flex items-center justify-center">
                  {images[imageIndex]?.url ? <img src={images[imageIndex].url} alt={images[imageIndex].label || design.name} className="w-full h-full object-contain object-center" /> : null}
                  {images.length > 1 ? <>
                    <button type="button" aria-label="Previous design image" onClick={() => setImageIndex((imageIndex - 1 + images.length) % images.length)} className="absolute left-4 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"><ChevronLeft size={20} /></button>
                    <button type="button" aria-label="Next design image" onClick={() => setImageIndex((imageIndex + 1) % images.length)} className="absolute right-4 w-10 h-10 rounded-full bg-white shadow flex items-center justify-center"><ChevronRight size={20} /></button>
                  </> : null}
                </div>
                {images.length > 1 ? <div className="flex gap-3 p-4 overflow-x-auto">{images.map((image, index) => <button key={`${image.url}-${index}`} type="button" onClick={() => setImageIndex(index)} className={`w-16 h-20 rounded-lg overflow-hidden border-2 shrink-0 ${index === imageIndex ? "border-[#F46524]" : "border-transparent"}`}><img src={image.url} alt={image.label || `View ${index + 1}`} className="w-full h-full object-contain object-center" /></button>)}</div> : null}
              </div>

              <div>
                <div className="font-label text-[#F46524] mb-4">StichB Approved Design</div>
                <h1 className="font-display text-4xl md:text-6xl tracking-tight">{design.name}</h1>
                <p className="text-xl font-semibold text-[#F46524] mt-4">{Number(design.basePrice||design.startingPrice||0)>0?`Starts from ₹${Number(design.basePrice||design.startingPrice).toLocaleString("en-IN")}`:"Price on request"}</p>
                <p className="text-[#5b5a56] mt-6 text-lg leading-relaxed">{design.description || "Custom stitching reference design"}</p>
                <div className="mt-8 rounded-2xl bg-[#FFF3E8] p-5 text-sm text-[#5b5a56]">This design will be attached to your booking and reviewed with your measurements and fabric preferences.</div>
                <button type="button" onClick={selectDesign} className="btn-primary mt-8"><Check size={18} /> Use this design</button>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

